import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, ArrowLeft, Loader2, Check } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { translations } from '../../translations';

interface ForgotPasswordProps {
  isDarkMode: boolean;
  language: string;
  onThemeChange?: (isDark: boolean) => void;
  setLanguage: (lang: string) => void;
}

export function ForgotPassword({ isDarkMode, language }: ForgotPasswordProps) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const t = translations[language as keyof typeof translations];

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke('send-reset-email', {
        body: { email }
      });

      if (fnError) {
        const msg = fnError.context?.error || fnError.message;
        throw new Error(msg);
      }

      if (data?.error) throw new Error(data.error);

      setSuccess(true);
    } catch (err: any) {
      console.error('Error resetting password:', err);
      setError(err.message || t.reset_email_error || 'Unable to send reset email. Please try again later');
    } finally {
      setIsLoading(false);
    }
  };

  const getWelcomeImage = () => {
    switch (language) {
      case 'FR':
        return 'https://ltyddaykojdpnqqlxhzz.supabase.co/storage/v1/object/public/avatars/welcome/FR.png';
      case 'EN':
        return 'https://ltyddaykojdpnqqlxhzz.supabase.co/storage/v1/object/public/avatars/welcome/EN.png';
      case 'ES':
        return 'https://ltyddaykojdpnqqlxhzz.supabase.co/storage/v1/object/public/avatars/welcome/ES.png';
      case 'RU':
        return 'https://ltyddaykojdpnqqlxhzz.supabase.co/storage/v1/object/public/avatars/welcome/RU.png';
      default:
        return 'https://ltyddaykojdpnqqlxhzz.supabase.co/storage/v1/object/public/avatars/welcome/EN.png';
    }
  };

  if (success) {
    return (
      <div className={`min-h-screen flex items-center justify-center p-4 ${isDarkMode ? 'bg-[#121212]' : 'bg-gray-50'}`}>
        <div className={`w-full max-w-md p-6 rounded-lg ${
          isDarkMode ? 'bg-[#1c1c1c] border border-[#2b2b2b]' : 'bg-white shadow-lg'
        }`}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
              <Check className="text-emerald-500" size={24} />
            </div>
            <h2 className={`text-xl font-semibold ${
              isDarkMode ? 'text-gray-200' : 'text-gray-900'
            }`}>
              {t.check_your_email}
            </h2>
          </div>
          
          <p className={`mb-6 text-sm ${
            isDarkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>
            {t.reset_link_sent}
          </p>

          <button
            onClick={() => navigate('/login')}
            className="w-full bg-orange-500 text-white rounded-lg py-2.5 text-sm font-medium hover:bg-orange-600 transition-colors"
          >
            {t.back_to_login}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen flex flex-col md:flex-row ${isDarkMode ? 'bg-[#121212]' : 'bg-gray-50'}`}>
      {/* Left side - Form */}
      <div className="w-full md:w-1/2 flex items-start justify-center p-6 md:p-8">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <button
              onClick={() => navigate('/login')}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode 
                  ? 'hover:bg-[#1c1c1c]' 
                  : 'hover:bg-gray-100'
              }`}
            >
              <ArrowLeft size={20} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
            </button>
            <h1 className={`text-2xl font-semibold ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              {t.forgot_password_title}
            </h1>
          </div>

          {error && (
            <div className="mb-6 p-3 rounded-lg bg-red-50 border border-red-100 text-red-600 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleResetPassword} className="space-y-4">
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                {t.email}
              </label>
              <div className="relative">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t.enter_email}
                  className={`w-full rounded-lg pl-10 pr-4 py-2.5 text-sm border ${
                    isDarkMode
                      ? 'bg-[#2b2b2b] text-gray-200 border-[#2b2b2b]'
                      : 'bg-white text-gray-900 border-gray-300'
                  } focus:outline-none focus:ring-2 focus:ring-orange-500`}
                  required
                />
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading || !email}
              className="w-full bg-orange-500 text-white rounded-lg py-2.5 text-sm font-medium hover:bg-orange-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  {t.sending}
                </>
              ) : (
                t.send_reset_link
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Right side - Welcome image */}
      <div 
        className="hidden md:block md:w-1/2 bg-cover bg-center"
        style={{
          backgroundImage: `url("${getWelcomeImage()}")`
        }}
      />
    </div>
  );
}